import { useCallback, useEffect, useState } from 'react';
import useApi from './useApi';
import { useAppSettings } from '../hooks/useAppSettings';
import { mapQueryProductMetaDataResponse } from '../factory/ProductFactory';

interface ProductMetaData {
    id: number;
    categoryName: string;
    tradeName: string | null;
    chartererId: number | null;
    chartererName: string | null;
    synonyms: string;
    contains?: string | null;
    isActive?: boolean;
}

interface QueryProductMetaDataResponse {
    items: any[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
}

export default function useGetProductsByCategory(categoryId?: number) {
    const { state, getApiData } = useApi();
    const { settings: appSettings } = useAppSettings();
    const [products, setProducts] = useState<ProductMetaData[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);

    const fetchProductsByCategory = useCallback(
        async (id: number, skipPagination: boolean = true) => {
            try {
                const apiUrl = appSettings?.api;
                if (!apiUrl) {
                    throw new Error('API URL not configured in app settings');
                }

                // Use QueryProductMetaData endpoint with categoryIds parameter
                // skipPagination=true requests all results without pagination
                let endpoint = `${apiUrl}QueryProductMetaData?categoryIds=${id}`;
                if (skipPagination) {
                    endpoint += '&skipPagination=true';
                } else {
                    // Default to large page size to avoid multiple requests for typical result sets
                    endpoint += '&pageSize=200';
                }
                await getApiData({ endpoint });
            } catch (error) {
                console.error(`[useGetProductsByCategory] Failed to fetch products for category ID ${id}:`, error);
                throw error;
            }
        },
        [getApiData, appSettings]
    );

    // Auto-fetch if categoryId is provided
    useEffect(() => {
        if (categoryId !== undefined) {
            fetchProductsByCategory(categoryId);
        }
    }, [categoryId, fetchProductsByCategory]);

    // Process API response
    useEffect(() => {
        if (state.status === 'success' && state.result) {
            // Use factory function to map the response (handles both paginated and legacy formats)
            const mappedProducts = mapQueryProductMetaDataResponse(state.result);
            setProducts(mappedProducts as any);

            // Extract totalCount from response if available
            const response = state.result as QueryProductMetaDataResponse;
            setTotalCount(response.totalCount || mappedProducts.length);
        } else if (state.status === 'error') {
            setProducts([]);
            setTotalCount(0);
        }
    }, [state]);

    return {
        products,
        totalCount,
        isLoading: state.isLoading,
        error: state.error,
        fetchProductsByCategory,
    };
}
